import { useEffect, useState } from 'react';
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SportsHandballIcon from '@mui/icons-material/SportsHandball';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import FemaleIcon from '@mui/icons-material/Female';
import { buildQueueSections, formatWaitingFor } from '../utils/queue';
import { suggestBalanceAdjustments, suggestImbalanceFixes } from '../utils/balanceAdjustments';

const IMBALANCE_THRESHOLD = 0.23; // diferença relativa a partir da qual sugerimos correções

const getPlayerPoints = (p) => p.totalPoints ?? (p.wins - p.losses);

// Selinhos de tipo + gênero (iguais aos cards do app).
function PlayerSeals({ player }) {
  const isSetter = player.playerType === 'Setter';
  const isAttacker = player.playerType === 'Attacker';
  const isBoth = player.playerType === 'Both';
  return (
    <>
      {(isAttacker || isBoth) && <SportsHandballIcon sx={{ ml: 0.4, color: 'primary.main', fontSize: 18 }} />}
      {(isSetter || isBoth) && <SportsVolleyballIcon sx={{ ml: 0.4, color: 'red', fontSize: 18 }} />}
      {player.gender === 'F' && <FemaleIcon sx={{ ml: 0.4, color: 'deeppink', fontSize: 18 }} />}
    </>
  );
}

function PlayerName({ player, variant = 'body1' }) {
  return (
    <Typography variant={variant} sx={{ display: 'flex', alignItems: 'center' }}>
      {player.playerName}
      <PlayerSeals player={player} />
    </Typography>
  );
}

// Modal "Montar próximo time" — sugestão da IA editável.
// onFillTeam(playerIds) é chamado ao confirmar (app -> dispatch; remoto -> comando).
export function NextTeamDialog({
  open, onClose, players, playersPerTeam, suggestion, config = {}, onFillTeam, onApplyAdjustment,
}) {
  const [nextTeam, setNextTeam] = useState([]);
  const [isCustom, setIsCustom] = useState(false);

  const suggestedIds = suggestion?.playerIds || [];
  const targetTeamId = suggestion?.targetTeamId || 1;

  useEffect(() => {
    if (open) {
      setNextTeam(suggestedIds);
      setIsCustom(false);
    }
  }, [open]);

  const targetCount = players.filter((p) => p.teamId === targetTeamId).length;
  const teamVacancy = Math.max(0, playersPerTeam - targetCount);

  const findPlayer = (id) => players.find((p) => p.playerId === id);
  const nextTeamPlayers = nextTeam.map(findPlayer).filter(Boolean);
  const nextTeamPoints = nextTeamPlayers.reduce((s, p) => s + getPlayerPoints(p), 0);
  const modalSections = buildQueueSections(players.filter((p) => !nextTeam.includes(p.playerId)));

  // Pontos dos dois times (o alvo já contando os jogadores selecionados).
  const opposingTeamId = targetTeamId === 1 ? 2 : 1;
  const teamPoints = (teamId) => players
    .filter((p) => p.teamId === teamId)
    .reduce((s, p) => s + getPlayerPoints(p), 0);
  const targetTotal = teamPoints(targetTeamId) + nextTeamPoints;
  const opposingTotal = teamPoints(opposingTeamId);

  const include = (playerId) => {
    if (nextTeam.length >= teamVacancy) return;
    setNextTeam([...nextTeam, playerId]);
    setIsCustom(true);
  };
  const remove = (playerId) => {
    setNextTeam(nextTeam.filter((id) => id !== playerId));
    setIsCustom(true);
  };
  const confirm = () => {
    onFillTeam(nextTeam);
    onClose();
  };

  const adjustment = suggestBalanceAdjustments({
    players,
    femalesPerTeam: config.femalesPerTeam,
    genderBalanceMode: config.genderBalanceMode,
    genderRestrictedMode: config.genderRestrictedMode,
    setterRestrictedMode: config.setterRestrictedMode,
    targetTeamId,
    nextTeamIds: nextTeam,
  });
  const acceptAdjustment = () => {
    if (onApplyAdjustment && adjustment) onApplyAdjustment(adjustment);
    onClose();
  };

  // Desequilíbrio de pontos (independente de gênero), quando o time está completo.
  const imbalanceRatio = Math.abs(targetTotal - opposingTotal)
    / Math.max(targetTotal, opposingTotal, 1);
  const teamComplete = nextTeamPlayers.length === teamVacancy && teamVacancy > 0;
  const showImbalance = opposingTotal > 0 && teamComplete && imbalanceRatio > IMBALANCE_THRESHOLD;
  const imbalanceFixes = showImbalance
    ? suggestImbalanceFixes({ players, targetTeamId, nextTeamIds: nextTeam })
    : null;
  const applyFix = (fix) => {
    if (onApplyAdjustment && fix) onApplyAdjustment(fix);
    onClose();
  };

  // Pontos resultantes dos dois times se a sugestão de equilíbrio for aceita.
  const adjustmentResult = () => {
    const opMap = new Map(adjustment.setTeamOps.map((op) => [op.playerId, op.teamId]));
    const pointsFor = (teamId) => players
      .filter((p) => (opMap.get(p.playerId) ?? p.teamId) === teamId)
      .reduce((s, p) => s + getPlayerPoints(p), 0);
    return { a: pointsFor(1), b: pointsFor(2) };
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Montar próximo time</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{
          bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 3, p: 2, mb: 2,
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>
            {`Próximo time (${isCustom ? 'Personalizado' : 'Sugestão da IA'})`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
            {`${nextTeamPlayers.length}/${teamVacancy} jogadores · ${targetTotal} pts vs ${opposingTotal} pts`}
          </Typography>
          {nextTeamPlayers.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Nenhum jogador selecionado.
            </Typography>
          )}
          {nextTeamPlayers.map((p) => (
            <Box
              key={p.playerId}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5,
              }}
            >
              <Box>
                <PlayerName player={p} />
                <Typography variant="caption" color="text.secondary">
                  {formatWaitingFor(p.waitingFor, p.checkedInNow)}
                </Typography>
              </Box>
              <Button size="small" color="error" onClick={() => remove(p.playerId)}>Remover</Button>
            </Box>
          ))}
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 1.5 }}
            disabled={nextTeamPlayers.length === 0}
            onClick={confirm}
          >
            Incluir time
          </Button>

          {adjustment && (
            <Box sx={{
              mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(237, 108, 2, 0.1)',
            }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Sugestão de equilíbrio
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {adjustment.headline}
              </Typography>
              {adjustment.moves.map((m) => (
                <Typography key={m} variant="body2">
                  •
                  {' '}
                  {m}
                </Typography>
              ))}
              <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'center', mt: 1 }}>
                {`Time A - ${adjustmentResult().a} pts vs Time B - ${adjustmentResult().b} pts`}
              </Typography>
              <Button
                variant="contained"
                color="warning"
                fullWidth
                sx={{ mt: 1 }}
                onClick={acceptAdjustment}
              >
                Aceitar sugestão
              </Button>
            </Box>
          )}

          {imbalanceFixes && (
            <Box sx={{
              mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255, 193, 7, 0.18)',
            }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Desequilíbrio detectado
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Por respeito à fila, o time atual é a melhor possibilidade de equilíbrio,
                porém o jogo seguiu desequilibrado. Ofereço algumas sugestões.
              </Typography>

              {[imbalanceFixes.suggestion1, imbalanceFixes.suggestion2, imbalanceFixes.suggestion3]
                .filter(Boolean)
                .map((fix, idx) => (
                  <Box key={fix.moves.join('|')} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {`${idx + 1}ª sugestão`}
                    </Typography>
                    {fix.moves.map((m) => (
                      <Typography key={m} variant="body2">
                        •
                        {' '}
                        {m}
                      </Typography>
                    ))}
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'center', mt: 0.5 }}>
                      {`Time A - ${fix.resultA} pts vs Time B - ${fix.resultB} pts`}
                    </Typography>
                    <Button
                      variant="contained"
                      color="warning"
                      fullWidth
                      sx={{ mt: 0.5 }}
                      onClick={() => applyFix(fix)}
                    >
                      Aceitar sugestão
                    </Button>
                  </Box>
                ))}
            </Box>
          )}
        </Box>

        {modalSections.map((section) => (
          <Box
            key={section.key}
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.04)', borderRadius: 3, p: 2, mb: 1.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center', mb: 1 }}>
              {section.title}
            </Typography>
            {section.players.map((p) => (
              <Box
                key={p.playerId}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5,
                }}
              >
                <Box>
                  <PlayerName player={p} variant="body2" />
                  <Typography variant="caption" color="text.secondary">
                    {formatWaitingFor(p.waitingFor, p.checkedInNow)}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" disabled={nextTeam.length >= teamVacancy} onClick={() => include(p.playerId)}>
                  Incluir
                </Button>
              </Box>
            ))}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
